/**
 * RFC-149: MotionReveal — Tier-1 ambient reveal wrapper.
 *
 * On first intersection, sets data-revealed="true" then disconnects the
 * observer (no leaks, no scroll listeners, no rAF). On
 * `prefers-reduced-motion: reduce`, short-circuits to revealed immediately
 * without ever creating an observer.
 *
 * Styling lives in src/index.css under `[data-marketing-reveal]`.
 */
import { useEffect, useRef, useState, type CSSProperties, type ElementType, type ReactNode } from 'react';

interface MotionRevealProps {
  children: ReactNode;
  /** Tag to render. Defaults to 'div'. */
  as?: ElementType;
  /** Optional class merged with reveal selector. */
  className?: string;
  /** Stagger child animations by this many ms (per index). */
  delayMs?: number;
  /** IntersectionObserver rootMargin override. */
  rootMargin?: string;
  /** Forward an inline style. */
  style?: CSSProperties;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function MotionReveal({
  children,
  as: Tag = 'div',
  className,
  delayMs,
  rootMargin = '0px 0px -10% 0px',
  style,
}: MotionRevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [revealed, setRevealed] = useState(() => prefersReducedMotion());

  useEffect(() => {
    if (revealed) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [revealed, rootMargin]);

  const composedStyle: CSSProperties = {
    ...(delayMs ? { transitionDelay: `${delayMs}ms` } : null),
    ...style,
  };

  return (
    <Tag
      ref={ref as never}
      data-marketing-reveal=""
      data-revealed={revealed ? 'true' : 'false'}
      className={className}
      style={composedStyle}
    >
      {children}
    </Tag>
  );
}
